import React, { useEffect, useState } from "react";
import Header from "./Header";
import Footer from "./Footer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { api } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Lock, Loader2, LogOut } from "lucide-react";

const TOKEN_KEY = "aip_admin_token";

export const useAdminAuth = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const logout = async () => {
    if (token) {
      try { await api.adminLogout(token); } catch (_) {}
    }
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };
  const save = (t) => {
    sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };
  return { token, save, logout };
};

const AdminGate = ({ children }) => {
  const { toast } = useToast();
  const { token, save } = useAdminAuth();
  const [checking, setChecking] = useState(!!token);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    let mounted = true;
    const verify = async () => {
      if (!token) { setChecking(false); return; }
      try {
        await api.adminVerify(token);
        if (mounted) setValid(true);
      } catch (_) {
        sessionStorage.removeItem(TOKEN_KEY);
        if (mounted) setValid(false);
      } finally {
        if (mounted) setChecking(false);
      }
    };
    verify();
    return () => { mounted = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setSubmitting(true);
    try {
      const res = await api.adminLogin(password);
      save(res.token);
      setValid(true);
      toast({ title: "Access granted", description: "Welcome to the Reservations Desk." });
    } catch (e) {
      toast({ title: "Access denied", description: e.response?.data?.detail || "Invalid password" });
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <Loader2 size={26} className="animate-spin text-[#c8102e]" />
      </div>
    );
  }

  if (valid) return children;

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 flex flex-col">
      <Header />
      <main className="flex-1 pt-32 pb-16">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border border-neutral-200 shadow-sm">
            <div className="p-8 text-center border-b border-neutral-100">
              <div className="w-14 h-14 mx-auto bg-[#c8102e]/10 text-[#c8102e] flex items-center justify-center mb-4">
                <Lock size={22} />
              </div>
              <span className="text-[11px] tracking-[0.25em] font-medium text-[#c8102e] uppercase">Staff Access</span>
              <h1 className="text-2xl font-serif font-medium mt-2">Reservations Desk</h1>
              <p className="text-sm text-neutral-600 mt-2">
                Enter the desk password to access bookings, cash collections and check-ins.
              </p>
            </div>
            <form onSubmit={submit} className="p-8 space-y-4">
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter desk password"
                  className="rounded-none"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={submitting || !password}
                className="w-full bg-[#c8102e] hover:bg-[#a70d26] text-white rounded-none py-5"
              >
                {submitting ? "Verifying..." : "Unlock desk"}
              </Button>
              <p className="text-xs text-neutral-500 text-center pt-2">
                For lost passwords contact the Duty Manager.
              </p>
            </form>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export const AdminLogoutButton = () => {
  const { logout } = useAdminAuth();
  return (
    <Button
      variant="outline"
      onClick={async () => { await logout(); window.location.reload(); }}
      className="rounded-none border-neutral-300 hover:border-[#c8102e] hover:text-[#c8102e]"
    >
      <LogOut size={14} className="mr-2" /> Sign out
    </Button>
  );
};

export default AdminGate;
